import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, StatCard } from "@/components/common/DashboardBits";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { NotesDialog, StatusSelect, DEFAULT_STATUSES, cap, fmtDate, inr } from "@/components/loanleads/LoanLeadBits";
import { CheckCircle2, Circle, Download, Loader2, Lock, PhoneCall, Search, StickyNote } from "lucide-react";
import { toast } from "sonner";

function ExportButton() {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const res = await api.get("/loan-leads/export", { responseType: "blob" });
      const name = (res.headers["content-disposition"] || "").match(/filename="?([^"]+)"?/)?.[1] || "loan-leads.xlsx";
      const url = URL.createObjectURL(res.data);
      const a = Object.assign(document.createElement("a"), { href: url, download: name });
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Excel downloaded");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <Button onClick={download} disabled={busy} className="bg-slate-950 hover:bg-slate-800" data-testid="export-loan-leads-button">
      {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />} Export to Excel
    </Button>
  );
}

function Filters({ q, setQ, status, setStatus, verified, setVerified, statuses }) {
  return (
    <div className="flex flex-col md:flex-row gap-2 mb-4">
      <div className="relative flex-1 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, phone, executive, bank, city…" className="pl-9" data-testid="loan-leads-search" />
      </div>
      <Select value={status} onValueChange={setStatus}>
        <SelectTrigger className="w-[170px]" data-testid="loan-leads-status-filter"><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value="all">All statuses</SelectItem>{statuses.map((s) => <SelectItem key={s} value={s}>{cap(s)}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={verified} onValueChange={setVerified}>
        <SelectTrigger className="w-[190px]" data-testid="loan-leads-verified-filter"><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value="all">Verified + unverified</SelectItem><SelectItem value="yes">Telecalling verified</SelectItem><SelectItem value="no">Not verified</SelectItem></SelectContent>
      </Select>
    </div>
  );
}

export default function LoanLeadsPage() {
  const [data, setData] = useState({ items: [], total: 0, stats: { verified: 0, by_status: {}, pipeline_inr: 0 }, statuses: [] });
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [verified, setVerified] = useState("all");
  const [notesFor, setNotesFor] = useState(null);

  const load = useCallback(() => {
    const params = { ...(q.trim() ? { q: q.trim() } : {}), ...(status !== "all" ? { status } : {}), ...(verified !== "all" ? { verified: verified === "yes" } : {}) };
    api.get("/loan-leads", { params }).then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [q, status, verified]);
  useEffect(() => { const h = setTimeout(load, 250); return () => clearTimeout(h); }, [load]);

  const update = async (item, patch) => {
    try {
      const { data: saved } = await api.patch(`/loan-leads/${item.visit_id}/${item.index}`, patch);
      setData((d) => ({ ...d, items: d.items.map((i) => (i.id === saved.id ? saved : i)) }));
      toast.success(patch.status ? `Marked ${cap(patch.status)}` : "Notes saved");
      if (patch.status) load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const converted = data.stats.by_status?.converted || 0;
  const statuses = useMemo(() => (data.statuses.length ? data.statuses : DEFAULT_STATUSES), [data.statuses]);

  return (
    <div data-testid="loan-leads-page">
      <PageHeader title="Loan Leads" subtitle="Telecalling-confirmed loan prospects collected by field executives during inspections" right={<ExportButton />} />
      <div className="mb-4">
        <span className="inline-flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs text-amber-900" data-testid="loan-leads-restricted-note">
          <Lock className="h-3.5 w-3.5" /> Restricted: contacts are visible to the Admin only.
        </span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard label="Total leads" value={data.total} tone="blue" testId="loan-leads-total" />
        <StatCard label="Telecalling verified" value={data.stats.verified} tone="green" testId="loan-leads-verified" />
        <StatCard label="Converted" value={converted} tone="amber" testId="loan-leads-converted" />
        <StatCard label="Open pipeline" value={inr(data.stats.pipeline_inr)} tone="default" testId="loan-leads-pipeline" />
      </div>
      <Filters q={q} setQ={setQ} status={status} setStatus={setStatus} verified={verified} setVerified={setVerified} statuses={statuses} />
      <Card className="border border-slate-200">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Customer</TableHead><TableHead>Phone</TableHead><TableHead className="text-right">Loan amount</TableHead><TableHead>Telecalling</TableHead>
              <TableHead>Follow-up</TableHead><TableHead>Collected by</TableHead><TableHead>Source visit</TableHead><TableHead>Captured</TableHead><TableHead />
            </TableRow></TableHeader>
            <TableBody>
              {data.items.map((it) => (
                <TableRow key={it.id} data-testid={`loan-lead-row-${it.id}`}>
                  <TableCell className="font-medium">{it.customer_name}</TableCell>
                  <TableCell><a href={`tel:${it.phone}`} className="inline-flex items-center gap-1 text-slate-800 hover:underline num"><PhoneCall className="h-3.5 w-3.5 text-slate-400" />{it.phone}</a></TableCell>
                  <TableCell className="text-right num">{inr(it.loan_amount_inr)}</TableCell>
                  <TableCell>{it.telecalling_verified ? <span className="inline-flex items-center gap-1 text-emerald-700 text-xs"><CheckCircle2 className="h-3.5 w-3.5" /> Verified</span> : <span className="inline-flex items-center gap-1 text-slate-400 text-xs"><Circle className="h-3.5 w-3.5" /> Pending</span>}</TableCell>
                  <TableCell><StatusSelect item={it} statuses={statuses} onChange={update} /></TableCell>
                  <TableCell className="text-sm">{it.executive_name}</TableCell>
                  <TableCell className="text-sm"><div>{it.source_customer}</div><div className="text-xs text-slate-500">{it.bank_name} · {it.city} {it.pincode !== "-" ? `· ${it.pincode}` : ""}</div></TableCell>
                  <TableCell className="text-xs text-slate-600 whitespace-nowrap">{fmtDate(it.captured_at)}</TableCell>
                  <TableCell>
                    <Button variant="ghost" size="sm" onClick={() => setNotesFor(it)} className={it.notes ? "text-amber-700" : "text-slate-500"} aria-label="Notes" data-testid={`loan-lead-notes-${it.id}`}><StickyNote className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
              {data.items.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-slate-500 py-8" data-testid="loan-leads-empty">No loan leads yet — they appear here once executives submit inspections.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      <NotesDialog item={notesFor} subtitle={notesFor ? `${notesFor.phone} · collected by ${notesFor.executive_name} during ${notesFor.source_customer}'s inspection` : ""} onClose={() => setNotesFor(null)} onSave={update} />
    </div>
  );
}
